import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import SoKnoLogo from "@/components/SoKnoLogo";
import {
  NAV_LINKS,
  PRODUCTION_NETWORK_PATH,
  PROJECTS_PATH,
  isProductionNetworkPath,
  linkHref,
  navTestId,
} from "@/constants/navLinks";

const SECTION_IDS = NAV_LINKS.filter((l) => l.hash).map((l) => l.hash);

function isStandaloneNavPath(pathname, link) {
  if (!link?.to || link.hash) return false;
  if (link.to === "/") return pathname === "/";
  return pathname === link.to || pathname.startsWith(`${link.to}/`);
}

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeHash, setActiveHash] = useState("home");
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";
  const onProductionNetwork = isProductionNetworkPath(location.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which home-page section is in view for active styling.
  useEffect(() => {
    if (!onHome) return undefined;

    const nodes = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveHash(visible[0].target.id);
        }
      },
      { rootMargin: "-28% 0px -55% 0px", threshold: [0.1, 0.25, 0.5] }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [onHome]);

  const closeMenu = () => setOpen(false);

  const isActive = (link) => {
    if (isStandaloneNavPath(location.pathname, link)) return true;
    if (link.to === PRODUCTION_NETWORK_PATH) return onProductionNetwork;
    if (link.to === PROJECTS_PATH && location.pathname.startsWith("/projects")) return true;
    if (!onHome) return false;
    if (link.hash) return activeHash === link.hash;
    return false;
  };

  const goToLink = (link) => (e) => {
    e.preventDefault();
    closeMenu();

    // Dedicated form routes — land at the form top via hash; page hook scrolls after mount.
    if (link.formHash) {
      navigate({ pathname: link.to, hash: link.formHash });
      return;
    }

    // Standalone routes (Projects, Press, etc.)
    if (link.to && !link.hash) {
      if (location.pathname !== link.to) navigate(link.to);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const hash = link.hash || "home";

    if (!onHome) {
      navigate({ pathname: "/", hash });
      return;
    }

    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveHash(hash);
      if (window.location.hash !== `#${hash}`) {
        window.history.replaceState(null, "", `/#${hash}`);
      }
    }
  };

  const linkClass = (link, mobile = false) => {
    const active = isActive(link);
    if (mobile) {
      return `font-script text-[12px] py-3 border-b transition-colors ${
        active
          ? "text-[var(--sokno-gold-warm)]"
          : "text-[var(--sokno-cream)] hover:text-[var(--sokno-gold-warm)]"
      }`;
    }
    return `font-script text-[10px] xl:text-[10.5px] transition-colors whitespace-nowrap ${
      active
        ? "text-[var(--sokno-gold-warm)]"
        : "text-[var(--sokno-cream-dim)] hover:text-[var(--sokno-gold-warm)]"
    }`;
  };

  return (
    <header
      data-testid="site-nav"
      className="fixed inset-x-0 top-0 z-50 transition-all"
      style={{
        backdropFilter: scrolled ? "blur(14px)" : "blur(6px)",
        WebkitBackdropFilter: scrolled ? "blur(14px)" : "blur(6px)",
        background: scrolled
          ? "linear-gradient(180deg, rgba(10,9,8,0.94), rgba(10,9,8,0.78))"
          : "linear-gradient(180deg, rgba(10,9,8,0.5), rgba(10,9,8,0.15))",
        borderBottom: scrolled
          ? "1px solid rgba(214, 175, 99, 0.18)"
          : "1px solid transparent",
      }}
    >
      <div className="container-x flex items-center justify-between gap-4" style={{ height: 84 }}>
        <a
          href="/#home"
          onClick={goToLink(NAV_LINKS[0])}
          className="flex items-center flex-shrink-0"
          data-testid="nav-logo-link"
        >
          <SoKnoLogo size={64} />
        </a>

        {/* Desktop links */}
        <nav
          className="hidden lg:flex items-center gap-3 xl:gap-5 2xl:gap-7 flex-1 justify-end min-w-0"
          aria-label="Primary"
        >
          {NAV_LINKS.map((l) => {
            const active = isActive(l);
            return (
              <a
                key={l.label}
                href={linkHref(l)}
                onClick={goToLink(l)}
                aria-current={active ? "page" : undefined}
                data-testid={navTestId(l.label)}
                className={linkClass(l)}
                style={{ letterSpacing: "0.16em" }}
              >
                {l.label}
              </a>
            );
          })}
        </nav>

        {/* Mobile toggle */}
        <button
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center flex-shrink-0"
          style={{
            width: 44,
            height: 44,
            border: "1px solid var(--sokno-line-strong)",
            color: "var(--sokno-gold)",
            borderRadius: 10,
            background: "rgba(214,175,99,0.04)",
          }}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div
          data-testid="nav-mobile-sheet"
          className="lg:hidden"
          style={{
            background: "rgba(10,9,8,0.98)",
            borderTop: "1px solid var(--sokno-line)",
          }}
        >
          <div className="container-x py-6 flex flex-col gap-2">
            {NAV_LINKS.map((l) => {
              const active = isActive(l);
              return (
                <a
                  key={l.label}
                  href={linkHref(l)}
                  onClick={goToLink(l)}
                  aria-current={active ? "page" : undefined}
                  data-testid={navTestId(l.label, "nav-mobile-link")}
                  className={linkClass(l, true)}
                  style={{
                    letterSpacing: "0.22em",
                    borderColor: "var(--sokno-line)",
                  }}
                >
                  {l.label}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}
